import { useCallback, useEffect, useRef, useState } from 'react';

import { useData } from '@/components/data-provider';
import { apiService } from '@/services/api-service';
import { dataService } from '@/services/data-service';
import { offlineSyncService, type SyncItem } from '@/services/offline-sync-service';
import { queryClient } from '@/utils/query-client';

export function useOfflineSync() {
	const [queue, setQueue] = useState<SyncItem[]>([]);
	const [isSyncing, setIsSyncing] = useState(false);
	const { isOnline } = useData();
	const processingRef = useRef(false);

	// Subscribe to queue changes
	useEffect(() => {
		const unsubscribe = offlineSyncService.subscribe((items) => {
			setQueue(items);
			setIsSyncing(offlineSyncService.isProcessing());
		});
		return unsubscribe;
	}, []);

	const processQueue = useCallback(async () => {
		if (!isOnline || offlineSyncService.getPendingCount() === 0) return;

		const result = await offlineSyncService.processQueue({
			uploadFile: apiService.uploadFile.bind(apiService),
			createAttachment: (data) => dataService.createAttachment(data as any),
			fetchApi: apiService.fetchApi.bind(apiService),
		});

		if (result.succeeded > 0) {
			await queryClient.refetchQueries();
		}

		return result;
	}, [isOnline]);

	// Auto-process queue whenever we are online and have pending work.
	// Previously this only fired on the offline→online edge, so items queued
	// from an online-time upload error (or carried over from a prior session
	// that started online) sat as "Waiting for connection" forever. Re-running
	// when `queue.length` changes also kicks the processor right after a new
	// item is enqueued.
	useEffect(() => {
		if (!isOnline || offlineSyncService.getPendingCount() === 0) return;
		if (processingRef.current) return;
		processingRef.current = true;
		void processQueue().finally(() => { processingRef.current = false; });
	}, [isOnline, processQueue, queue.length]);

	const removeItem = useCallback(async (id: string) => {
		await offlineSyncService.removeItem(id);
	}, []);

	const pendingCount = queue.filter(
		i => i.status === 'pending' || i.status === 'failed' || i.status === 'syncing',
	).length;

	const failedCount = queue.filter(
		i => i.status === 'failed' && i.retryCount >= 5,
	).length;

	return {
		queue,
		isSyncing,
		pendingCount,
		failedCount,
		processQueue,
		removeItem,
	};
}
